/**
 * AdjacencyMapSyncStatusPopover (WQ-210)
 *
 * Click-to-expand detail panel for the sync status chip. Three sections:
 *   1. Current state — health, last sync, next scheduled, interval, enabled
 *   2. Recent runs — last 10 from /sync/history, errors expanded inline
 *   3. Sync contract — static list of what IS and ISN'T in scope
 *
 * Source of truth for the contract is FIELD_MAPPINGS in
 * platform-api/app/services/sharepoint_sync_service.py — keep in sync
 * if that file changes (stable; bumped last in WQ-209).
 */
import React from 'react';
import type { AdjacencyMapSyncStatus } from '../../hooks/useAdjacencyMapSyncStatus';

interface Props {
  status: AdjacencyMapSyncStatus;
  onClose: () => void;
}

function formatAbs(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return iso;
  const diff = Date.now() - d;
  const absMin = Math.abs(Math.floor(diff / 60_000));
  if (absMin < 1) return diff >= 0 ? 'just now' : 'in <1m';
  if (diff >= 0) {
    if (absMin < 60) return `${absMin}m ago`;
    const h = Math.floor(absMin / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  }
  if (absMin < 60) return `in ${absMin}m`;
  const h = Math.floor(absMin / 60);
  return `in ${h}h`;
}

// Sync contract — mirrors FIELD_MAPPINGS in sharepoint_sync_service.py
const BIDI_FIELDS = [
  'Title (name)',
  'Description',
  'Strategic Initiative',
  'Phase',
  'Status',
  'Priority Number (priority)',
  'Est R&D',
  'Est Rev',
  'MRD',
  'MRD Target',
  'ATD',
  'ATQ',
  'ATS',
  'Dev Lane',
  'Dev Team',
];
const SP_ONLY_FIELDS = [
  'Priority (sp_priority_tag)',
  'Product Line',
  'Product Manager (pull-only; display name resolution WQ-211)',
];
const NOT_SYNCED = [
  'Node position on the map (map_position_x/y)',
  'Parent / edge relationships',
  'Financial assumptions (avgFteCost, iwtSouthCommitment, revenueProjection, …)',
  'Platform-only metadata (source, sp_list_item_id)',
];

const AdjacencyMapSyncStatusPopover: React.FC<Props> = ({ status, onClose }) => {
  return (
    <div
      className="modal-overlay"
      data-testid="sync-status-popover"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface, #1a1a1a)',
          border: '1px solid var(--border, rgba(255,255,255,0.1))',
          borderRadius: 8,
          padding: 20,
          maxWidth: 720,
          width: '90%',
          maxHeight: '85vh',
          overflowY: 'auto',
          color: 'var(--text-primary, #fff)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>SharePoint Sync Status</h3>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            aria-label="Close"
            data-testid="sync-popover-close"
          >
            ✕
          </button>
        </div>

        {/* Section 1 — current state */}
        <section style={{ marginBottom: 16 }}>
          <h4 style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--text-secondary)' }}>
            Current State
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, fontSize: 13 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Health</div>
              <div data-testid="sync-popover-health">{status.health}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Enabled</div>
              <div>{status.enabled ? 'yes' : 'no'}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Last scheduled sync</div>
              <div title={formatAbs(status.lastScheduledSync)}>
                {formatRelative(status.lastScheduledSync)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Next scheduled run</div>
              <div title={formatAbs(status.nextRunTime)}>
                {formatRelative(status.nextRunTime)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Interval</div>
              <div>{status.intervalMinutes} min</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Summary</div>
              <div>{status.summary}</div>
            </div>
          </div>
        </section>

        {/* Section 2 — recent runs */}
        <section style={{ marginBottom: 16 }}>
          <h4 style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--text-secondary)' }}>
            Last {status.recentRuns.length} runs
          </h4>
          {status.recentRuns.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              No sync history yet.
            </div>
          ) : (
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }} data-testid="sync-popover-runs">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>When</th>
                  <th style={{ textAlign: 'right', padding: '4px 8px' }}>Pulled</th>
                  <th style={{ textAlign: 'right', padding: '4px 8px' }}>Pushed</th>
                  <th style={{ textAlign: 'right', padding: '4px 8px' }}>Conflicts</th>
                  <th style={{ textAlign: 'right', padding: '4px 8px' }}>Skipped</th>
                  <th style={{ textAlign: 'right', padding: '4px 8px' }}>Dur</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {status.recentRuns.map((r) => {
                  const hasErrors = (r.errors?.length ?? 0) > 0 || (r.status && r.status !== 'success');
                  return (
                    <React.Fragment key={r.id}>
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '4px 8px' }} title={formatAbs(r.created_at)}>
                          {formatRelative(r.created_at)}
                        </td>
                        <td style={{ padding: '4px 8px', textAlign: 'right' }}>{r.pulled}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right' }}>{r.pushed}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right' }}>{r.conflicts}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right' }}>{r.skipped}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                          {r.duration_ms != null ? `${r.duration_ms}ms` : '—'}
                        </td>
                        <td style={{ padding: '4px 8px' }}>
                          {hasErrors ? (
                            <span style={{ color: 'var(--error, #ef4444)' }}>
                              {r.status || 'error'}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--success, #10b981)' }}>✓</span>
                          )}
                        </td>
                      </tr>
                      {hasErrors && (r.errors?.length ?? 0) > 0 && (
                        <tr>
                          <td
                            colSpan={7}
                            style={{
                              padding: '4px 8px 8px',
                              background: 'rgba(239,68,68,0.06)',
                              fontFamily: 'monospace',
                              fontSize: 11,
                              color: 'var(--error, #ef4444)',
                            }}
                          >
                            {r.errors!.map((err, i) => (
                              <div key={i}>{err}</div>
                            ))}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>

        {/* Section 3 — sync contract */}
        <section data-testid="sync-popover-contract">
          <h4 style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--text-secondary)' }}>
            Sync Contract
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, fontSize: 12 }}>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--success, #10b981)' }}>
                Bidirectional ({BIDI_FIELDS.length})
              </div>
              <ul style={{ margin: 0, paddingLeft: 16, color: 'var(--text-secondary)' }}>
                {BIDI_FIELDS.map((f) => <li key={f}>{f}</li>)}
              </ul>
            </div>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--accent, #3b82f6)' }}>
                SharePoint → Platform only ({SP_ONLY_FIELDS.length})
              </div>
              <ul style={{ margin: 0, paddingLeft: 16, color: 'var(--text-secondary)' }}>
                {SP_ONLY_FIELDS.map((f) => <li key={f}>{f}</li>)}
              </ul>
            </div>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--text-muted, #888)' }}>
                NOT synced ({NOT_SYNCED.length})
              </div>
              <ul style={{ margin: 0, paddingLeft: 16, color: 'var(--text-secondary)' }}>
                {NOT_SYNCED.map((f) => <li key={f}>{f}</li>)}
              </ul>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdjacencyMapSyncStatusPopover;
